import { useState, useEffect, useRef, useCallback } from 'react';
import { useSubscription } from '@apollo/react-hooks';
import CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION from '../GraphQl/Subscriptions/CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION';

// Debug flag - set to true for development logging
const DEBUG = process.env.NODE_ENV === 'development';

// Stale threshold - if no updates for this long while active, consider connection stale
const STALE_THRESHOLD_MS = 30000; // 30 seconds
// How often to check for stale connection
const STALE_CHECK_INTERVAL_MS = 5000; // 5 seconds
// Delay before re-enabling subscription after reconnect
const RECONNECT_DELAY_MS = 100;

const useCampaignLaunchProgress = (campaignOrderId, options = {}) => {
  const {
    enabled = true,
    onProgress,
    onCompleted,
    onFailed,
    onError,
    autoCleanup = true,
    autoReconnect = true // Enable auto-reconnection by default
  } = options;

  const [progressData, setProgressData] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [skipSubscription, setSkipSubscription] = useState(false);

  const cleanup = useRef(null);
  const lastUpdateTime = useRef(null);
  const staleCheckInterval = useRef(null);

  // Force reconnect by toggling subscription
  const reconnect = useCallback(() => {
    if (DEBUG) console.log('[useCampaignLaunchProgress] Reconnecting subscription...');
    setSkipSubscription(true);
    // Re-enable after a brief delay to force new connection
    setTimeout(() => {
      setSkipSubscription(false);
      setIsStale(false);
      setReconnectCount(prev => prev + 1);
    }, RECONNECT_DELAY_MS);
  }, []);

  const { data, loading, error } = useSubscription(
    CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION,
    {
      variables: { campaignOrderId },
      skip: !enabled || !campaignOrderId || skipSubscription,
      onSubscriptionData: ({ subscriptionData }) => {
        if (subscriptionData?.data?.campaignLaunchProgress) {
          const progress = subscriptionData.data.campaignLaunchProgress;

          // Update last update time for stale detection
          lastUpdateTime.current = Date.now();
          setIsStale(false);

          // Set progress data
          setProgressData(progress);

          // Track if campaign is actively launching
          const activePhases = ['STARTING', 'DATE_VALIDATION', 'INSERTION_ORDER', 'PACKAGE', 'LINE_ITEMS', 'CREATIVES'];
          const isLaunching = activePhases.includes(progress.phase);
          
          if (isLaunching && !isActive) {
            setIsActive(true);
            setStartTime(Date.now());
            if (progress.metadata?.estimatedDuration) {
              setEstimatedDuration(progress.metadata.estimatedDuration);
            }
          }

          // Handle progress callbacks
          if (onProgress) {
            onProgress(progress);
          }

          // Handle completion
          if (progress.phase === 'COMPLETED') {
            setIsActive(false);
            if (onCompleted) {
              onCompleted(progress);
            }
            if (autoCleanup) {
              scheduleCleanup();
            }
          }

          // Handle failure
          if (progress.phase === 'FAILED') {
            setIsActive(false);
            if (onFailed) {
              onFailed(progress);
            }
            if (autoCleanup) {
              scheduleCleanup();
            }
          }
        }
      },
      onError: (subscriptionError) => {
        console.error('Campaign launch progress subscription error:', subscriptionError);
        if (onError) {
          onError(subscriptionError);
        }
      }
    }
  );

  // Schedule cleanup after completion/failure
  const scheduleCleanup = () => {
    if (cleanup.current) {
      clearTimeout(cleanup.current);
    }
    
    cleanup.current = setTimeout(() => {
      setProgressData(null);
      setIsActive(false);
      setStartTime(null);
      setEstimatedDuration(null);
    }, 30000); // Clean up after 30 seconds
  };

  // Calculate elapsed time
  const elapsedTime = startTime ? Date.now() - startTime : 0;
  
  // Calculate estimated remaining time
  const estimatedRemainingTime = 
    estimatedDuration && progressData?.progress > 0
      ? Math.max(0, estimatedDuration - elapsedTime)
      : null;

  // Format time duration in minutes and seconds
  const formatDuration = (milliseconds) => {
    if (!milliseconds) return null;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanup.current) {
        clearTimeout(cleanup.current);
      }
      if (staleCheckInterval.current) {
        clearInterval(staleCheckInterval.current);
      }
    };
  }, []);

  // Stale connection detection and auto-reconnect
  useEffect(() => {
    // Only run stale detection when subscription is active and in a launching phase
    if (!enabled || !campaignOrderId || !isActive) {
      if (staleCheckInterval.current) {
        clearInterval(staleCheckInterval.current);
        staleCheckInterval.current = null;
      }
      return;
    }

    // Initialize lastUpdateTime if not set
    if (!lastUpdateTime.current) {
      lastUpdateTime.current = Date.now();
    }

    staleCheckInterval.current = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - (lastUpdateTime.current || Date.now());

      if (timeSinceLastUpdate > STALE_THRESHOLD_MS) {
        if (DEBUG) {
          console.warn(
            `[useCampaignLaunchProgress] Connection appears stale. No updates for ${Math.round(timeSinceLastUpdate / 1000)}s`
          );
        }
        setIsStale(true);

        if (autoReconnect) {
          if (DEBUG) console.log('[useCampaignLaunchProgress] Auto-reconnecting...');
          reconnect();
        }
      }
    }, STALE_CHECK_INTERVAL_MS);

    return () => {
      if (staleCheckInterval.current) {
        clearInterval(staleCheckInterval.current);
        staleCheckInterval.current = null;
      }
    };
  }, [enabled, campaignOrderId, isActive, autoReconnect, reconnect]);

  return {
    // Progress data
    progressData,

    // Status indicators
    isActive,
    isLoading: loading,
    hasError: !!error,
    error,

    // Connection health
    isStale,
    reconnectCount,

    // Progress details
    phase: progressData?.phase,
    progress: progressData?.progress || 0,
    message: progressData?.message || '',
    step: progressData?.step,
    current: progressData?.current,
    total: progressData?.total,
    metadata: progressData?.metadata,

    // Time tracking
    elapsedTime,
    estimatedRemainingTime,
    startTime,

    // Formatted times
    elapsedTimeFormatted: formatDuration(elapsedTime),
    estimatedRemainingTimeFormatted: formatDuration(estimatedRemainingTime),

    // Helper functions
    isCompleted: progressData?.phase === 'COMPLETED',
    isFailed: progressData?.phase === 'FAILED',
    isPossibleTimeout: progressData?.metadata?.possibleTimeout || false,

    // Manual reconnect
    reconnect,

    // Manual cleanup
    cleanup: () => {
      setProgressData(null);
      setIsActive(false);
      setStartTime(null);
      setEstimatedDuration(null);
      setIsStale(false);
      lastUpdateTime.current = null;
    }
  };
};

export default useCampaignLaunchProgress;